import { KeyRound, Webhook } from 'lucide-react';

import { defineTour, type TourResolver } from '@nucleus-commerce/admin-kit/tour';
import { getAdminV1StoresStoreIdWebhooks } from '@nucleus-commerce/api-client/admin';

export const apiKeysTour = defineTour({
  id: 'settings.api-keys',
  version: 1,
  group: 'developers',
  route: '/_authed/_active/s/$storeId/settings/api-keys',
  scope: 'store',
  requires: 'api_keys.manage',
  icon: KeyRound,
  steps: [
    {
      id: 'read-only',
      kind: 'explain',
      target: 'settings.read-only',
      optional: true,
      waitMs: 1000,
    },
    { id: 'intro', kind: 'explain', target: 'settings.api-keys.intro' },
    {
      id: 'keys',
      kind: 'explain',
      variants: [
        { target: 'settings.api-keys.table' },
        { target: 'settings.api-keys.empty', copy: 'empty' },
      ],
    },
    { id: 'create', kind: 'explain', target: 'settings.api-keys.create', optional: true },
  ],
  related: ['settings.webhooks'],
});

export const webhooksTour = defineTour({
  id: 'settings.webhooks',
  version: 1,
  group: 'developers',
  route: '/_authed/_active/s/$storeId/settings/webhooks/',
  scope: 'store',
  requires: 'webhooks.manage',
  icon: Webhook,
  steps: [
    {
      id: 'read-only',
      kind: 'explain',
      target: 'settings.read-only',
      optional: true,
      waitMs: 1000,
    },
    { id: 'intro', kind: 'explain', target: 'settings.webhooks.intro' },
    {
      id: 'endpoints',
      kind: 'explain',
      variants: [
        { target: 'settings.webhooks.table' },
        { target: 'settings.webhooks.empty', copy: 'empty' },
      ],
    },
    { id: 'add', kind: 'explain', target: 'settings.webhooks.add', optional: true },
  ],
  related: ['settings.webhook-detail', 'settings.api-keys'],
});

/** The first endpoint of the store: the detail tour runs on it. */
const firstWebhookEndpoint: TourResolver = {
  key: 'first-webhook-endpoint',
  thing: 'things.webhookEndpoint',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const { items } = await getAdminV1StoresStoreIdWebhooks({ storeId });
    const endpoint = items[0];
    return endpoint ? { storeId, endpointId: endpoint.id } : null;
  },
};

export const webhookDetailTour = defineTour({
  id: 'settings.webhook-detail',
  version: 1,
  group: 'developers',
  route: '/_authed/_active/s/$storeId/settings/webhooks/$endpointId',
  scope: 'record',
  resolve: firstWebhookEndpoint,
  requires: 'webhooks.manage',
  icon: Webhook,
  steps: [
    {
      id: 'read-only',
      kind: 'explain',
      target: 'settings.read-only',
      optional: true,
      waitMs: 1000,
    },
    { id: 'details', kind: 'explain', target: 'settings.webhook.details' },
    { id: 'send-test', kind: 'explain', target: 'settings.webhook.send-test', optional: true },
    { id: 'deliveries', kind: 'explain', target: 'settings.webhook.deliveries' },
    { id: 'actions', kind: 'explain', target: 'settings.webhook.actions', optional: true },
  ],
  related: ['settings.webhooks'],
});
